import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React, { useEffect, useState } from 'react';
import PolicyApi from '../api/PolicyApi';
import { AuthData, useAuthContext, withAuthContext } from '../context/auth';
import { useConfigContext } from '../context/config';
import { CurrencyCode } from '../types/currencyCode';
import { tableStyles } from '../util/styles';
import { getComparator, HeadCell, Order, stableSort } from '../util/tableUtil';
import { formatCurrency, pascalCase } from '../util/util';
import { StyledEmptyMessage } from './common/index';
import Loading from './Loading';
import SortableTable from './SortableTable';

interface Props {
	transactions: any;
	policyLocator: string;
	loading?: boolean;
	authData: AuthData;
	currency: CurrencyCode;
}

interface Data {
	type: string;
	perilName: string;
	amount: number;
	currency: CurrencyCode;
}

function createData(
	type: string,
	perilName: string,
	amount: string,
	currency: CurrencyCode,
): Data {
	const amt = parseFloat(amount);
	return { type, perilName, amount: amt, currency };
}

const TransactionsDetailTable = (props: Props) => {
	const { transactions, loading, currency, policyLocator } = props;
	const AuthContext = useAuthContext();
	const ConfigContext = useConfigContext();
	const [feeConfigs, setFeeConfigs] = useState([]);

	useEffect(() => {
		const fetchPolicy = async () => {
			try {
				const policy = await PolicyApi.getPolicy(
					AuthContext.auth.authorizationToken,
					policyLocator,
				);
				const productName = policy.data.productName;
				const config = ConfigContext.productConfigs.find(
					(c) => c.name === productName,
				);
				const feeConfig = config.policyConfiguration.fees;
				setFeeConfigs(feeConfig);
			} catch {
				setFeeConfigs([]);
			}
		};
		fetchPolicy();
	}, []);

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('postedTimestamp');
	const classes = tableStyles();
	const loadingMsg = loading ? (
		<StyledEmptyMessage>
			<Loading />
		</StyledEmptyMessage>
	) : null;
	const emptyTable = !transactions.length ? (
		<StyledEmptyMessage>No Transactions</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{ id: 'type', rightAlign: false, label: 'Type' },
		{ id: 'perilName', rightAlign: false, label: 'Name' },
		{ id: 'amount', rightAlign: true, label: 'Amount' },
	];

	const rows: any[] = transactions.map((t: any) => {
		let name = '';
		switch (t.type) {
			case 'premium':
				name = t.perilName;
				break;
			case 'tax':
				name = `${t.taxName} (${t.perilName})`;
				break;
			case 'fee':
				const feeConfig = feeConfigs.find((f) => f.name === t.feeName);
				name = feeConfig ? feeConfig.displayName : t.feeName;
				break;
			default:
				name = t.perilName;
				break;
		}
		return createData(t.type, name, t.amount, currency);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			const key = `${row.type} ${row.perilName} ${row.amount} ${index}`;

			return (
				<TableRow hover tabIndex={-1} key={key} className={classes.tableRow}>
					<TableCell>{pascalCase(row.type ?? '')}</TableCell>
					<TableCell>{pascalCase(row.perilName ?? '')}</TableCell>
					<TableCell align='right'>
						{formatCurrency(parseFloat(row.amount), currency)}
					</TableCell>
				</TableRow>
			);
		},
	);

	const additionalMsg = loading
		? loadingMsg
		: !transactions.length
		? emptyTable
		: null;

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={additionalMsg}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withAuthContext<Props>(TransactionsDetailTable);
